export const createDefaultFocalPoint = (width, height) => {
  return {
    leftPixel: Math.round(width / 2),
    topPixel: Math.round(height / 2),
  };
};

export const createCloudinaryUrlForComponent = (imageDomain, url) => {
  let tempUrl = url;
  const startsWithASlash = tempUrl.startsWith('/');
  if (startsWithASlash) tempUrl = tempUrl.substring(1);
  return `${imageDomain}/image/upload/{+transform}${tempUrl}`;;
}

export const getCloudinaryImageUrl = (imageDomain, image, format = 'jpg') => {
  const bothHeightAndWidth = image?.width > 0 && image?.height > 0;
  const x = Math.round(image?.focalPoint?.leftPixel);
  const y = Math.round(image?.focalPoint?.topPixel);
  const hasFocalPoint = x && y;

  // Set crop
  let crop = '';
  if (bothHeightAndWidth) {
    crop = `c_fill,${hasFocalPoint ? `g_xy_center,x_${x},y_${y},` : 'g_auto,'}`;
  } else {
    crop = 'c_scale,';
  }

  // if aspect ratio
  let fixedRatio;
  if (image.ratio) {
    const { a = 2, b = 3, length = 500 } = image.ratio;
    const ab = `${a}:${b}`;
    const ba = `${b}:${a}`;
    fixedRatio = `if_ar_lt_1.0/c_fill,g_auto,h_${length},ar_${ab}/if_else/c_fill,g_auto,w_${length},ar_${ba}/if_end/`;
  }

  const shape =
    fixedRatio ||
    `${crop}${image?.width ? `w_${image?.width},` : ''}${
      image?.height ? `h_${image?.height},` : ''
    }`;

  // Set quality
  const quality = image?.quality ? `q_${image?.quality}` : 'q_auto';

  // Set image url
  const url = image?.relativeSrc || image?.url;

  return `${imageDomain}/image/upload/${shape}f_${format},${quality}${url}`;
};

const getCloudinaryImageSource = (imageDomain, image, type, media) => {
  return {
    media,
    srcSet: getCloudinaryImageUrl(imageDomain, image, type),
    type: `image/${type}`,
  };
};

export const getCloudinaryImageSources = (imageDomain, image, mediaQuery) => [
  getCloudinaryImageSource(imageDomain, image, 'avif', mediaQuery),
  getCloudinaryImageSource(imageDomain, image, 'jp2', mediaQuery),
  getCloudinaryImageSource(imageDomain, image, 'webp', mediaQuery),
  getCloudinaryImageSource(imageDomain, image, 'jpg', mediaQuery),
];

export const getCloudinaryTopImageSources = (imageDomain, topImage) => {
  const focalX = topImage.width ? `,x_${Math.round(topImage.width * 0.5)}` : '';
  const focalY = topImage.height
    ? `,y_${Math.round(topImage.height * 0.61)}`
    : '';
  const gravity = focalX || focalY ? ',g_xy_center' : '';
  return [
    {
      media: '(min-width: 1680px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_730,w_1920,f_avif${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/avif',
    },
    {
      media: '(min-width: 1680px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_730,w_1920,f_jp2${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jp2',
    },
    {
      media: '(min-width: 1680px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_730,w_1920,f_webp${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/webp',
    },
    {
      media: '(min-width: 1680px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_730,w_1920,f_jpg${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jpeg',
    },
    {
      media: '(max-width: 1679px) and (min-width: 1366px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_640,w_1680,f_avif${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/avif',
    },
    {
      media: '(max-width: 1679px) and (min-width: 1366px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_640,w_1680,f_jp2${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jp2',
    },
    {
      media: '(max-width: 1679px) and (min-width: 1366px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_640,w_1680,f_webp${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/webp',
    },
    {
      media: '(max-width: 1679px) and (min-width: 1366px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_640,w_1680,f_jpg${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jpeg',
    },
    {
      media: '(max-width: 1365px) and (min-width: 1025px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_536,w_1365,f_avif${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/avif',
    },
    {
      media: '(max-width: 1365px) and (min-width: 1025px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_536,w_1365,f_jp2${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jp2',
    },
    {
      media: '(max-width: 1365px) and (min-width: 1025px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_536,w_1365,f_webp${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/webp',
    },
    {
      media: '(max-width: 1365px) and (min-width: 1025px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_536,w_1365,f_jpg${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jpeg',
    },
    {
      media: '(max-width: 1024px) and (min-width: 975px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_480,w_1024,f_avif${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/avif',
    },
    {
      media: '(max-width: 1024px) and (min-width: 975px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_480,w_1024,f_jp2${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jp2',
    },
    {
      media: '(max-width: 1024px) and (min-width: 975px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_480,w_1024,f_webp${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/webp',
    },
    {
      media: '(max-width: 1024px) and (min-width: 975px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_480,w_1024,f_jpg${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jpeg',
    },
    {
      media: '(max-width: 974px) and (min-width: 769px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_360,w_975,f_avif${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/avif',
    },
    {
      media: '(max-width: 974px) and (min-width: 769px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_360,w_975,f_jp2${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jp2',
    },
    {
      media: '(max-width: 974px) and (min-width: 769px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_360,w_975,f_webp${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/webp',
    },
    {
      media: '(max-width: 974px) and (min-width: 769px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_360,w_975,f_jpg${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jpeg',
    },
    {
      media: '(max-width: 768px) and (min-width: 416px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_350,w_768,f_avif${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/avif',
    },
    {
      media: '(max-width: 768px) and (min-width: 416px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_350,w_768,f_jp2${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jp2',
    },
    {
      media: '(max-width: 768px) and (min-width: 416px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_350,w_768,f_webp${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/webp',
    },
    {
      media: '(max-width: 768px) and (min-width: 416px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_350,w_768,f_jpg${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jpeg',
    },
    {
      media: '(max-width: 415px) and (min-width: 376px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_210,w_414,f_avif${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/avif',
    },
    {
      media: '(max-width: 415px) and (min-width: 376px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_210,w_414,f_jp2${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jp2',
    },
    {
      media: '(max-width: 415px) and (min-width: 376px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_210,w_414,f_webp${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/webp',
    },
    {
      media: '(max-width: 415px) and (min-width: 376px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_210,w_414,f_jpg${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jpeg',
    },
    {
      media: '(max-width: 376px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_315,w_375,f_avif${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/avif',
    },
    {
      media: '(max-width: 376px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_315,w_375,f_jp2${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jp2',
    },
    {
      media: '(max-width: 376px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_315,w_375,f_webp${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/webp',
    },
    {
      media: '(max-width: 376px)',
      srcSet: `${imageDomain}/image/upload/c_fill,h_315,w_375,f_jpg${gravity},q_auto:eco${focalX}${focalY}${topImage.relativeSrc}`,
      type: 'image/jpeg',
    },
  ];
};

export const mapCloudinaryGalleryImage = (image, imageDomain) => {
  const portrait = image.width && image.height && image.height > image.width;
  return {
    ...image,
    text: image?.text || image?.alt,
    thumbnail: `${imageDomain}/image/upload/c_scale,h_85,f_auto,q_auto:eco${image.relativeSrc}`,
    type: 'image',
    uri: image.src,
    sources:
      image.relativeSrc !== null
        ? [
            ...getCloudinaryImageSources(
              imageDomain,
              { ...image, height: undefined, width: 375 },
              '(max-width: 375px)',
            ),
            ...getCloudinaryImageSources(
              imageDomain,
              { ...image, height: undefined, width: 414 },
              '(min-width: 376px) and (max-width: 414px)',
            ),
            ...getCloudinaryImageSources(
              imageDomain,
              { ...image, height: undefined, width: 768 },
              '(min-width: 415px) and (max-width: 768px)',
            ),
            ...getCloudinaryImageSources(
              imageDomain,
              { ...image, height: undefined, width: portrait ? 560 : 1280 },
              '(min-width: 768px)',
            ),
          ]
        : [],
    splashSources:
      image.relativeSrc !== null
        ? [
            ...getCloudinaryImageSources(
              imageDomain,
              { ...image, height: undefined, width: 450 },
              '(min-width: 1280px)',
            ),
            ...getCloudinaryImageSources(
              imageDomain,
              { ...image, height: undefined, width: 300 },
              '(min-width: 768px)',
            ),
            ...getCloudinaryImageSources(
              imageDomain,
              { ...image, height: undefined, width: 200 },
              '(min-width: 0px)',
            ),
          ]
        : [],
  };
};

export const getCloudinaryBadgeSources = (imageDomain, image) => {
  const { relativeSrc, mobileSize, desktopSize } = image;
  const desktopSizes = desktopSize
    ? [
        {
          srcSet: `
        ${imageDomain}/image/upload/c_scale,h_${desktopSize},f_webp${relativeSrc},
      `,
          type: 'image/webp',
          media: '(min-width: 768px)',
        },
        desktopSize && {
          srcSet: `
        ${imageDomain}/image/upload/c_scale,h_${desktopSize},f_jpg${relativeSrc},
      `,
          type: 'image/jpeg',
          media: '(min-width: 768px)',
        },
      ]
    : [];

  return [
    ...desktopSizes,
    {
      srcSet: `
        ${imageDomain}/image/upload/c_scale,h_${mobileSize},f_webp${relativeSrc},
      `,
      type: 'image/webp',
    },
    {
      srcSet: `
        ${imageDomain}/image/upload/c_scale,h_${mobileSize},f_jpg${relativeSrc},
      `,
      type: 'image/jpeg',
    },
  ];
};
